import useApiService from '~/composables/api/useApiService';
import { apiEndpoints } from '@/constants';
import type {
  ApiError,
  CreateViewBody,
  DashboardViewModel,
  DuplicateViewBody,
  ReorderViewsBody,
  RenameViewBody,
  SavedView,
  SaveViewBody,
  UpdateViewBody
} from '~/types';
import { TRANSACTIONS_ALL_TABLE_IDENTIFIER } from '~/types/views';

const toSavedView = (model: DashboardViewModel): SavedView => {
  const { name, ...config } = model.ViewContent;
  return {
    id: model.Id,
    name,
    isDefault: model.IsDefault,
    scope: model.Scope,
    position: model.Position,
    config
  };
};

export default function useViewsApi() {
  const { get, post, put, patch, del } = useApiService();

  const getViews = async () => {
    const { data, error } = await get(apiEndpoints.views, 'getViews', {
      query: { tableIdentifier: TRANSACTIONS_ALL_TABLE_IDENTIFIER }
    });

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return ((data.value ?? []) as DashboardViewModel[])
      .map(toSavedView)
      .sort((a, b) => a.position - b.position);
  };

  const createView = async (body: CreateViewBody) => {
    const payload: SaveViewBody = {
      TableIdentifier: TRANSACTIONS_ALL_TABLE_IDENTIFIER,
      IsDefault: false,
      ViewContent: { name: body.name, ...body.config }
    };

    const { data, error } = await post(apiEndpoints.views, payload, 'createView');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return toSavedView(data.value as DashboardViewModel);
  };

  const updateView = async (id: string, body: UpdateViewBody) => {
    const { data, error } = await put(`${apiEndpoints.views}/${id}`, body, 'updateView');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return toSavedView(data.value as DashboardViewModel);
  };

  const renameView = async (id: string, body: RenameViewBody) => {
    const { data, error } = await patch(`${apiEndpoints.views}/${id}/name`, body, 'renameView');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return toSavedView(data.value as DashboardViewModel);
  };

  const duplicateView = async (id: string, body: DuplicateViewBody) => {
    const { data, error } = await post(
      `${apiEndpoints.views}/${id}/duplicate`,
      body,
      'duplicateView'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return toSavedView(data.value as DashboardViewModel);
  };

  const deleteView = async (id: string) => {
    const { error } = await del(`${apiEndpoints.views}/${id}`, 'deleteView');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  const setDefaultView = async (id: string) => {
    const { data, error } = await patch(
      `${apiEndpoints.views}/${id}/default`,
      {},
      'setDefaultView'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return toSavedView(data.value as DashboardViewModel);
  };

  const reorderViews = async (
    orderedIds: string[],
    tableIdentifier: string = TRANSACTIONS_ALL_TABLE_IDENTIFIER
  ) => {
    const body: ReorderViewsBody = {
      tableIdentifier,
      orderedIds
    };
    const { error } = await put(`${apiEndpoints.views}/reorder`, body, 'reorderViews');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
  };

  return {
    getViews,
    createView,
    updateView,
    renameView,
    duplicateView,
    deleteView,
    setDefaultView,
    reorderViews
  };
}
